import { Head, Link, router } from "@inertiajs/react"
import { Edit, Plus, Trash2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property, Tenant } from "@/types"

interface Props {
  property: Property
  tenants: Tenant[]
}

export default function Index({ property, tenants }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: adminPropertiesPath() },
    { title: property.name, href: `/admin/properties/${property.id}` },
    { title: "Tenants", href: `/admin/properties/${property.id}/tenants` },
  ]

  const handleDelete = (tenantId: number) => {
    if (confirm("Remove this tenant?")) {
      router.delete(`/admin/properties/${property.id}/tenants/${tenantId}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Tenants — ${property.name}`} />

      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Tenant Roster</h1>
            <p className="text-muted-foreground text-sm">
              Used to populate the site plan & co-tenants page on Marketing brochures.
            </p>
          </div>
          <Button render={<Link href={`/admin/properties/${property.id}/tenants/new`} />}>
            <Plus className="mr-2 h-4 w-4" /> Add Tenant
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{property.name} — Tenants ({tenants.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {tenants.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Plus />
                  </EmptyMedia>
                  <EmptyTitle>No tenants yet</EmptyTitle>
                  <EmptyDescription>Add tenants to see them on the site-plan brochure page.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead className="text-right">Sq Ft</TableHead>
                    <TableHead>Dimensions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">{t.marker_number ?? "—"}</TableCell>
                      <TableCell>{t.name}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{t.address ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        {t.area_sqft ? Number(t.area_sqft).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-sm">{t.dimensions ?? "—"}</TableCell>
                      <TableCell>
                        {t.available ? (
                          <Badge variant="default">Available</Badge>
                        ) : (
                          <Badge variant="secondary">Leased</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            render={<Link href={`/admin/properties/${property.id}/tenants/${t.id}/edit`} />}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(t.id)}>
                            <Trash2 className="text-destructive h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
